import { useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Badge, Box, Button, Group, Paper, Skeleton, Stack, Text, Title } from '@mantine/core';
import { EditOrganizationModal, Icon, ProgramTabs } from '@/components';
import { BULLETIN_TYPE, CUSTOM_ICONS, ROUTE_ALIASES } from '@/types';
import { CHURCH_TYPE, type Church, type ChurchUpdateRequest } from '@/types/churches';
import type { MyParishBulletinUpdateRequest } from '@/types/myParish';
import styles from './OrganizationDetail.module.sass';
import { formatPhoneNumber } from '@/helpers';
import { ChurchContext, TabStatusProvider } from '@/shared';
import { useTabStatus } from '@/hooks';
import { useChurch, useUpdateChurchMutation } from '@/hooks/churches';
import { useMyParish, useUpdateMyParishMutation } from '@/hooks/organization-programs';

const LAYOUT = {
  SKELETON_HEIGHT: 20,
  SKELETON_ROW_COUNT: 10,
  SKELETON_BUTTON_WIDTH: 120,
  SIDEBAR_WIDTH: 400,
  ARROW_LEFT_ICON_SIZE: 16,
  ORGANIZATION_TYPE_ICON_SIZE: 64,
};

const getOrganizationTypeIcon = (type: string) => {
  const iconMap: Record<string, CUSTOM_ICONS> = {
    [CHURCH_TYPE.DIOCESE]: CUSTOM_ICONS.DIOCESE_BUILDING_SOLID,
    [CHURCH_TYPE.PARISH]: CUSTOM_ICONS.CHURCH_SOLID,
    [CHURCH_TYPE.CLUSTER]: CUSTOM_ICONS.CHURCH_SOLID,
  };

  return iconMap[type] || CUSTOM_ICONS.CHURCH_SOLID;
};

export const OrganizationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { tabStatus, refreshTabStatus, isLoading: isTabStatusLoading } = useTabStatus();
  const { data: church, isLoading } = useChurch(id);
  const { data: myParishData } = useMyParish(id);
  const updateChurchMutation = useUpdateChurchMutation();
  const updateMyParishMutation = useUpdateMyParishMutation(id ?? '');
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const handleBackClick = () => {
    const cameFromOrganizations = Boolean(
      (location.state as { fromOrganizations?: boolean } | null)?.fromOrganizations
    );

    if (cameFromOrganizations) {
      navigate(-1);
      return;
    }

    navigate(ROUTE_ALIASES.ORGANIZATIONS);
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
  };

  const handleEditModalSave = async (updatedOrganization: ChurchUpdateRequest) => {
    if (!id || !church) {
      throw new Error('Church ID or church data not available');
    }
    await updateChurchMutation.mutateAsync({ id, data: updatedOrganization });
    setIsEditModalOpen(false);
  };

  const handleSaveBulletin = async (bulletinData: MyParishBulletinUpdateRequest) => {
    if (!id) {
      throw new Error('Church ID not available');
    }
    await updateMyParishMutation.mutateAsync({
      isActive: myParishData?.isActive ?? false,
      isHiddenFromApp: myParishData?.isHiddenFromApp ?? false,
      isDioceseControl: myParishData?.isDioceseControl ?? false,
      isEnhanced: myParishData?.isEnhanced ?? false,
      bulletin: bulletinData,
      comment: myParishData?.comment,
    });
    refreshTabStatus();
  };

  const formatAddress = (church: Church) => {
    return (
      <Stack gap={0}>
        {church?.addressLine1 && <Text className={styles.valueText}>{church.addressLine1}</Text>}
        {church?.addressLine2 && <Text className={styles.valueText}>{church.addressLine2}</Text>}
        {church?.city && (
          <Text className={styles.valueText}>
            {church?.city}
            {church?.state && `, ${church.state}`}
            {church?.zipCode && ` ${church.zipCode}`}
          </Text>
        )}
        {church?.country && <Text className={styles.valueText}>{church.country}</Text>}
      </Stack>
    );
  };

  const formatPhone = (phone?: string | { prefix?: string; number?: string }) => {
    if (!phone) return null;
    const combined =
      typeof phone === 'string'
        ? phone
        : phone.prefix && phone.number
          ? `${phone.prefix}${phone.number}`
          : phone.number || phone.prefix || '';
    const formatted = formatPhoneNumber(combined);
    return formatted || null;
  };

  if (isLoading) {
    return (
      <Stack mx="xxl" py="md" gap="md" w="400">
        <Group>
          <Skeleton height={LAYOUT.SKELETON_HEIGHT} width={LAYOUT.SKELETON_BUTTON_WIDTH} radius="sm" />
        </Group>
        <Paper p="xl" radius="md">
          <Stack gap="lg">
            <Group>
              <Skeleton height={LAYOUT.SKELETON_HEIGHT} width={LAYOUT.SKELETON_BUTTON_WIDTH} radius="sm" />
              <Stack gap="xs">
                <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="60%" radius="sm" />
                <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="40%" radius="sm" />
              </Stack>
            </Group>
            <Stack gap="md">
              {Array.from({ length: LAYOUT.SKELETON_ROW_COUNT }).map((_, index) => (
                <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="50%" radius="sm" key={index} />
              ))}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (!church) {
    return (
      <Stack mx="xxl" py="md" gap="md" w={LAYOUT.SIDEBAR_WIDTH}>
        <Group>
          <Button
            leftSection={<Icon name="IconArrowLeft" size={LAYOUT.ARROW_LEFT_ICON_SIZE} />}
            variant="default"
            onClick={handleBackClick}
          >
            Back to Organizations
          </Button>
        </Group>
        <Paper p="xl" radius="md">
          <Text>Church not found</Text>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack mx="xxl" py="md" gap="md">
      <Group>
        <Button
          leftSection={<Icon name="IconArrowLeft" size={LAYOUT.ARROW_LEFT_ICON_SIZE} />}
          variant="default"
          onClick={handleBackClick}
        >
          Back to Organizations
        </Button>
      </Group>

      <Group gap="xl" align="flex-start">
        <Box className={styles.sidebar} w={LAYOUT.SIDEBAR_WIDTH}>
          <Paper p="xl" radius="md">
            <Stack gap="xl">
              <Group justify="flex-end" gap="xxs">
                <Button
                  size="xs"
                  onClick={handleEditClick}
                  className={styles.editButton}
                  leftSection={<Icon name="IconEdit" size={LAYOUT.ARROW_LEFT_ICON_SIZE} />}
                  variant="default"
                >
                  Edit
                </Button>
              </Group>

              <Stack align="center" gap="md">
                <Icon
                  name={getOrganizationTypeIcon(church.type || '')}
                  size={LAYOUT.ORGANIZATION_TYPE_ICON_SIZE}
                  className={styles.churchTypeIcon}
                />
                <Title order={3} ta="center">
                  {church.fullName || 'No name available'}
                </Title>
              </Stack>

              <Stack gap="lg">
                {church.fullName && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Full Name</Text>
                    <Text className={styles.valueText}>{church.fullName}</Text>
                  </Stack>
                )}

                {church.shortName && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Short Name</Text>
                    <Text className={styles.valueText}>{church.shortName}</Text>
                  </Stack>
                )}

                {church.type && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Type</Text>
                    <Text className={styles.valueText}>{church.type}</Text>
                  </Stack>
                )}

                <Stack gap="xs">
                  <Text className={styles.labelText}>Address</Text>
                  {formatAddress(church)}
                </Stack>

                {formatPhone(church.phone) && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Phone</Text>
                    <Text className={styles.valueText}>{formatPhone(church.phone)}</Text>
                  </Stack>
                )}

                {church.diocese?.name && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Diocese</Text>
                    <Text className={styles.valueText}>{church.diocese.name}</Text>
                  </Stack>
                )}

                {church.website && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Website</Text>
                    <Text className={styles.valueText}>
                      <a href={church.website} target="_blank" rel="noopener noreferrer">
                        {church.website}
                      </a>
                    </Text>
                  </Stack>
                )}

                {church.timezone && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Timezone</Text>
                    <Text className={styles.valueText}>{church.timezone}</Text>
                  </Stack>
                )}

                {church.cluster?.name && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Cluster</Text>
                    <Text className={styles.valueText}>{church.cluster.name}</Text>
                  </Stack>
                )}

                {church.givingName && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Giving Name</Text>
                    <Text className={styles.valueText}>{church.givingName}</Text>
                  </Stack>
                )}

                {church.latitude != null && church.longitude != null && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Coordinates</Text>
                    <Text className={styles.valueText}>
                      {church.latitude}, {church.longitude}
                    </Text>
                  </Stack>
                )}

                {church.discoverMassId && (
                  <Stack gap="xs">
                    <Text className={styles.labelText}>Discover Mass ID</Text>
                    <Text className={styles.valueText}>{church.discoverMassId}</Text>
                  </Stack>
                )}

                {myParishData && (
                  <Group gap="md" align="center">
                    <Stack gap="xs">
                      <Text className={styles.labelText}>Bulletin Type</Text>
                      <Badge variant="default">
                        {myParishData.bulletin.type === BULLETIN_TYPE.DIOCESAN && 'Diocesan'}
                        {myParishData.bulletin.type === BULLETIN_TYPE.LPI && 'LPI'}
                        {myParishData.bulletin.type === BULLETIN_TYPE.OTHER && 'Other'}
                        {myParishData.bulletin.type === BULLETIN_TYPE.NONE && 'None'}
                      </Badge>
                    </Stack>
                    {myParishData.bulletin.type !== BULLETIN_TYPE.NONE && myParishData.bulletin.number && (
                      <Stack gap="xs">
                        <Text className={styles.labelText}>Bulletin Number</Text>
                        <Text className={styles.valueText}>{myParishData.bulletin.number}</Text>
                      </Stack>
                    )}
                  </Group>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Box>

        <Box flex="1">
          <TabStatusProvider tabStatus={tabStatus} refreshTabStatus={refreshTabStatus} isLoading={isTabStatusLoading}>
            <ProgramTabs />
            <Box mt="md">
              <ChurchContext.Provider value={{ church }}>
                <Outlet />
              </ChurchContext.Provider>
            </Box>
          </TabStatusProvider>
        </Box>
      </Group>

      <EditOrganizationModal
        opened={isEditModalOpen}
        onClose={handleEditModalClose}
        organization={church}
        onSave={handleEditModalSave}
        myParishData={myParishData}
        onSaveBulletin={handleSaveBulletin}
      />
    </Stack>
  );
};
